import React, { useRef, useEffect } from "react";

import "./Disclosure.css";

const PrivacyPolicy = ({ onScrollToBottom }) => {
  const privacyPolicyRef = useRef(null);

  useEffect(() => {
    const content = privacyPolicyRef.current;

    if (content) {
      const isScrollable = content.scrollHeight > content.clientHeight;

      // If not scrollable, enable checkbox by default
      if (!isScrollable) {
        onScrollToBottom();
      } else {
        const handleScroll = () => {
          const { scrollTop, scrollHeight, clientHeight } = content;
          if (scrollTop + clientHeight >= scrollHeight - 5) {
            onScrollToBottom();
          }
        };

        content.addEventListener("scroll", handleScroll);

        return () => content.removeEventListener("scroll", handleScroll);
      }
    }
  }, [onScrollToBottom]);

  return (
    <div className={"purchase-text purchase-content"} ref={privacyPolicyRef}>
      <br />

      <div className={"purchase-body"}>
        <div className={"purchase-section"}>
          <div>
            <p>
              <b>1. General.</b> This <a href="https://www.sarlit.com/privacy-policy" target="_blank" rel="noopener noreferrer">Privacy Policy</a> describes the information
              we collect, process and use to support www.sarlit.com, the SAR
              Platform and all other websites, landing pages, features, apps,
              technologies, accounts, platforms and software offered by us
              (collectively, “Websites”). Additional information on our use of
              your data and Cookies can be found in our <a href="https://www.sarlit.com/terms-conditions" target="_blank" rel="noopener noreferrer">Terms and Conditions</a>. If
              you have any questions or concerns regarding this <a href="https://www.sarlit.com/privacy-policy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
              or our Websites, please contact us by email at info@sarlit.com. If
              you are a California resident, please see our Privacy Notice for
              California Residents in addition to this <a href="https://www.sarlit.com/privacy-policy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>.
            </p>
            <p>
              <b>2. Cookies.</b> Cookies are small pieces of text used to store
              information on web browsers. Cookies are used to store and receive
              identifiers and other information on computers, phones, and other
              devices. Other technologies, including data we store on your web
              browser or device, identifiers associated with your device, and
              other software, are used for similar purposes. We use Cookies on
              your device and receive information stored in Cookies when you
              access and use our Websites. Our use of Cookies may include, but
              is not limited to, improving our Websites (for example, tracking
              how you use our Websites so we can learn how to improve them),
              verifying your account (for example, keeping you logged in as you
              navigate between pages) and providing a safer user experience (for
              example, detecting security threats). We also allow our
              third-party partners to place Cookies on your device. Our
              third-party partners can place Cookies on your device when you:
              (1) use their sites and/or apps in connection with our Websites;
              and (2) access and use our Websites. Our third-party partners may
              have access to your data (including personal data) via cookies.
              You may have the ability to restrict the use of Cookies by
              changing the settings on your account, web browser and/or device.
            </p>
            <p>
              <b>3. Information We Collect.</b> We collect and use the following
              kinds of information from you: (1) information you provide, such
              as personal data (including your name, email, phone number,
              address, employer and job title), content, communications and
              other information you provide when you access and use our
              Websites, including when you sign up for an account; (2) your
              usage, such as information about how you use our Websites (for
              example, the types of content you view or engage with, the
              features you use, the actions you take, the people or accounts you
              interact with and the time, frequency and duration of your
              activities); (3) your transactions on our Websites; (4) account
              information (including username and password); and (5) device
              information, such as unique device IDs/identifiers, data from your
              device and cookie data stored on your device.
            </p>
            <p>
              <b>4. Our Use.</b> We use the information we collect from you to:
              (1) provide, personalize and improve our Websites and Products;
              (2) send you advertisements, offers and other sponsored content;
              (3) track your usage of our Websites; (4) monitor your activity on
              our Websites; (5) create, maintain, verify and secure your account
              with us; (6) promote safety, integrity and security across our
              Websites; (7) facilitate purchases and transactions on our
              Websites; (8) fulfill the purpose for which you provided the
              information; (9) provide you with any products and services you
              have requested; and (10) communicate with you by sending you (a)
              marketing communications, (b) information about our Websites and
              Products or third party products and services, (c) notices
              regarding our <a href="https://www.sarlit.com/terms-conditions" target="_blank" rel="noopener noreferrer">Terms and Conditions</a> and this <a href="https://www.sarlit.com/privacy-policy" target="_blank" rel="noopener noreferrer">Privacy Policy</a> and (d)
              responses to your inquiries.
            </p>
            <p>
              <b>5. Sharing Information.</b> The information we collect from you
              may be shared with third party partners such as analytic service
              providers (for example, Google Analytics) and service providers
              that support our operations.
            </p>
            <p>
              <b>6. Storage.</b> We maintain personal information that we
              collect from you in secure cloud storage environments hosted by
              third party cloud providers in the United States of America. Your
              information may be maintained, used and processed by us and our
              third party partners which, notwithstanding the foregoing, may be
              located in various locations around the world.
            </p>
            <p>
              <b>7. Managing/Deleting Information.</b> You have the right to
              access the personal information we have collected from you. You
              can restrict the use of your personal information and request the
              deletion of data we collected from you by contacting us at
              info@sarlit.com.
            </p>
            <p>
              <b>8. Legal Requests for Information.</b> In response to a legal
              request (like a search warrant, court order or subpoena) we will
              share your information with the requesting party if we have a good
              faith belief that the law requires us to do so. This may include
              responding to legal requests from jurisdictions outside of the
              United States when we have a good-faith belief that the response
              is required by law in that jurisdiction, affects users in that
              jurisdiction, and is consistent with internationally recognized
              standards. We will also share your information when we have a
              good-faith belief it is necessary to: detect, prevent and address
              fraud, unauthorized use of our Websites, violations of our terms
              or policies, or other harmful or illegal activity; to protect
              ourselves (including our rights, property, Websites or Products),
              you or others, including as part of investigations or regulatory
              inquiries; or to prevent death or imminent bodily harm.
            </p>
            <p>
              <b>9. Updates.</b> We have the right to update, modify or amend
              this <a href="https://www.sarlit.com/privacy-policy" target="_blank" rel="noopener noreferrer">Privacy Policy</a> from time to time, within our sole and
              absolute discretion. Once any updated, modified or amended Privacy
              Policy is in effect, it will be posted at www.sarlit.com and you
              will be bound by it if you continue to use or access any of our
              Websites or purchase or use any of our Products. The most recent
              version of this <a href="https://www.sarlit.com/privacy-policy" target="_blank" rel="noopener noreferrer">Privacy Policy</a> in effect will always be posted at
              www.sarlit.com. If you do not agree with any updated, modified or
              amended <a href="https://www.sarlit.com/privacy-policy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>, you must immediately cease: (i) using or
              accessing any of our Websites; and (ii) receiving, purchasing or
              using any of our Products.
            </p>

            <h4>PRIVACY NOTICE FOR CALIFORNIA RESIDENTS</h4>
            <br />
            <br />

            <p>
              General: This California Privacy Notice (“Notice”) is for
              California residents and supplements our <a href="https://www.sarlit.com/privacy-policy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>. It
              explains how we collect and use your Personal Information and how
              to exercise your rights under the California Consumer Privacy Act
              (“CCPA”). The term “Personal Information” in this Notice means
              information that identifies, relates to, describes, is reasonably
              capable of being associated with, or could reasonably be linked,
              directly or indirectly, with you. Personal Information does not
              include information that is aggregated or information that cannot
              be reasonably linked to you.{" "}
            </p>
            <br />
            <br />

            <p>
              Personal Information Categories: Below is a summary of the
              CCPA-related categories of your Personal Information that we may
              have collected, sold and/or disclosed for a business purpose over
              the past 12 months (depending on how you use our Websites). Also
              included below is a description of how we use your Personal
              Information and the sources of your Personal Information. For more
              information on how we collect and use your Personal Information,
              return to our <a href="https://www.sarlit.com/privacy-policy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>.
            </p>
            <br />
            <br />
            <p>
              <table width="696" border="1px">
                <tbody>
                  <thead>
                    <tr>
                      <th width="348">
                        <p>
                          <strong>
                            Categories of Personal Information we collect, sell
                            and/or disclose for a business purpose
                          </strong>
                        </p>
                      </th>
                      <th width="174">
                        <p>
                          <strong>How we use your Personal Information</strong>
                        </p>
                      </th>
                      <th width="174">
                        <p>
                          <strong>Sources of your Personal Information</strong>
                        </p>
                      </th>
                    </tr>
                  </thead>
                  <tr>
                    <td width="348">
                      <p>
                        - Identifiers, such as a real name, alias, postal
                        address, unique personal identifier, online identifier,
                        IP address, email address, account name, Social Security
                        number, driver&rsquo;s license number, passport number,
                        or other similar identifiers
                      </p>
                      <p>
                        - Personal information categories listed in the
                        California Customer Records statute (Cal. Civ. Code
                        &sect; 1798.80(e)), such as a name, signature, Social
                        Security number, physical characteristics or
                        description, address, telephone number, passport number,
                        driver&rsquo;s license or state identification card
                        number, insurance policy number, education, employment,
                        employment history, bank account number, credit card
                        number, debit card number, or any other financial
                        information, medical information, or health insurance
                        information. Some personal information included in this
                        category may overlap with other categories.
                      </p>
                      <p>
                        - Commercial information, including records of personal
                        property, products or services purchased, obtained or
                        considered, or other purchasing or consuming histories
                        or tendencies
                      </p>
                      <p>
                        - Internet or other electronic network activity
                        information, including, but not limited to: browsing
                        history, search history, and information regarding a
                        consumer&rsquo;s interaction with a website,
                        application, or advertisement
                      </p>
                      <p>- Geolocation data</p>
                      <p>- Professional or employment-related information</p>
                      <p>
                        - Account information (including username and password)
                      </p>
                    </td>
                    <td width="174">
                      <p>
                        - Providing, personalizing and improving our Websites
                        and Products
                      </p>
                      <p>- Facilitating purchases and transactions</p>
                      <p>- Measurement and analytics</p>
                      <p>- Monitoring your activity on our Websites</p>
                      <p>- Communicating with you</p>
                      <p>
                        - Promoting safety, integrity, and security across our
                        Websites
                      </p>
                      <p>
                        - Sending you marketing communications, advertisements,
                        offers and other sponsored content
                      </p>
                      <p>
                        - Sending you information about our Websites and
                        Products or third party products and services
                      </p>
                      <p>
                        - Creating, maintaining, verifying and securing your
                        account with us
                      </p>
                      <p>
                        - To fulfill the purpose for which you provided the
                        information
                      </p>
                      <p>
                        - Providing you with any products and services you have
                        requested
                      </p>
                    </td>
                    <td width="174">
                      <p>- You (information you provide)</p>
                      <p>
                        - Your devices (for example, your computer and phone)
                      </p>
                      <p>
                        - Cookies stored on your device by us and third party
                        partners
                      </p>
                      <p>
                        - Our partners (for example, analytic service providers)
                      </p>
                      <p>&nbsp;</p>
                    </td>
                  </tr>
                </tbody>
              </table>
            </p>

            <p>
              Your CCPA Rights: Under the CCPA, you have the following rights:
              (1) Right to Know: You have the right to obtain the Personal
              Information we collect, use, sell or disclose; (2) Stop Selling:
              You have the right to stop us from selling your Personal
              Information; (3) Request Deletion: You have the right to request
              that we delete your Personal Information; and (4)
              Non-Discrimination: We will not discriminate against you for
              exercising any of these rights.
            </p>

            <p>
              If you have any questions or would like to exercise your CCPA
              rights, please contact us by email at info@sarlit.com.
            </p>
          </div>
        </div>
      </div>
      <br />
    </div>
  );
};

export default PrivacyPolicy;



// WEBPACK FOOTER //
// ./src/ace/PrivacyPolicy.js